import { Link } from "react-router-dom";
import sectionFiveHero from "../.././assets/images/section-five-hero.png";
import Ellipse from "../.././assets/images/Ellipse.png";

const AttestationButton = ({ text, isHighlighted = false }) => (
  <div
    className={`border-l-4 ${isHighlighted ? "border-primary-red" : "border-gray-300"
      } pl-4 py-2`}
  >
    <button className="text-left text-primary-navybluefont-extrabold hover:text-primary-red transition-colors font-bold text-[#06213F]">
      {text}
    </button>
  </div>
);

const ServiceCard = ({
  image,
  title,
  subtitle,
  bgColor = "bg-primary-navyblue",
  textColor = "text-primary-white",
  link
}) => {
  const cardContent = (
    <div
      className={`relative overflow-hidden rounded-[20px] h-[280px] ${bgColor} ${textColor} group cursor-pointer`}
    >
      {image && (
        <div className="absolute inset-0">
          <img src={image} alt={title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-primary-navyblue bg-opacity-40 transition-opacity duration-300 group-hover:bg-opacity-50"></div>
        </div>
      )}
      <div className="relative z-10 p-6 h-full flex flex-col justify-end">
        {subtitle && (
          <p className="text-2xl text-center font-extrabold mb-20 opacity-90">{subtitle}</p>
        )}
        <h3 className="text-2xl font-extrabold">{title}</h3>
      </div>
    </div>
  );

  if (link) {
    return link.startsWith('http') ? (
      <a href={link} target="_blank" rel="noopener noreferrer" className="block h-full">
        {cardContent}
      </a>
    ) : (
      <Link to={link} className="block h-full">
        {cardContent}
      </Link>
    );
  }

  return cardContent;
};

export default function DocumentAttestationSection({ attestationData = null, specializedData = null }) {
  const fallbackAttestationServices = [
    { text: "Qatar Embassy Attestation", highlighted: true },
    { text: "Saudi Embassy Attestation", highlighted: true },
    { text: "MOFA Attestation", highlighted: true },
    { text: "UAE Embassy Attestation", highlighted: true },
    { text: "Kuwaiti Embassy Attestation", highlighted: true },
  ];

  const attestationServices = attestationData?.items && attestationData.items.length > 0
    ? attestationData.items.map(item => ({ text: item, highlighted: true }))
    : fallbackAttestationServices;

  return (
    <section className="bg-white">
      {/* Document Attestation Section */}
      <div className="font-manrope max-w-7xl mx-auto lg:px-6 md:px-12 px-4 md:pb-0 md:pt-10 pt-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
           
            <div className="md:order-1 order-2 relative w-full overflow-hidden">
              <img
                src={attestationData?.image_url || sectionFiveHero}
                alt="Document Attestation"
                className="w-full h-auto min-h-[400px] lg:min-h-[600px] object-cover object-center mx-auto scale-100"
              />
            </div>
            <div className="md:order-2 order-1">
              {attestationData?.title ? (
                <div dangerouslySetInnerHTML={{ __html: attestationData.title }}></div>
              ) : (
                <h2 className="xl:text-5xl md:text-4xl text-3xl font-extrabold leading-tight mb-6">
                  <span className="text-primary-navyblue block mb-2">Reliable  <span className="text-primary-red">Document</span></span>
                  <span className="text-primary-red">Attestation <span className="text-primary-navyblue">Services</span></span>
                </h2>
              )}

              <p className="font-medium text-gray-600 mb-8 text-lg">
                {attestationData?.description || "Our mission is to simplify your journey, from applying for your dream job to settling abroad with confidence."}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {attestationServices.map((service, index) => (
                  <AttestationButton
                    key={index}
                    text={service.text}
                    isHighlighted={service.highlighted}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Specialised Services Section */}
      <div className="bg-primary-lightblue py-10 ">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* Left - Text Content */}
            <div>
              {specializedData?.title ? (
                <div dangerouslySetInnerHTML={{ __html: specializedData.title }}></div>
              ) : (
                <h2 className="xl:text-5xl md:text-4xl text-3xl font-extrabold leading-tight mb-4">
                  <span className="text-primary-navyblue block mb-2">Explore</span>
                  <span className="text-primary-red">especialised services</span>
                </h2>
              )}

              <p className="font-medium text-primary-navyblue text-md leading-relaxed">
                {specializedData?.description || "Discover a wide range of value-added services, from document attestation to premium visa support, designed to make your overseas journey smoother, faster, and completely hassle-free."}
              </p>
            </div>

            {/* Right - Services Grid */}
            <div className="grid md:grid-cols-2 gap-10">
              {/* Big Data Card - spans 2 columns in first row */}
              <div className="col-span-1">
                <ServiceCard
                  title={specializedData?.cards?.[0]?.title || "Data Flow"}
                  subtitle="BIG DATA"
                  bgColor="bg-indigo-900"
                  link={specializedData?.cards?.[0]?.link}
                  image={specializedData?.cards?.[0]?.image_url}
                />
              </div>

              {/* Prometric Exam */}
              <div className="col-span-1">
                <ServiceCard
                  title={specializedData?.cards?.[1]?.title || "Prometric Exam"}
                  image={specializedData?.cards?.[1]?.image_url || "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"}
                  link={specializedData?.cards?.[1]?.link}
                />
              </div>
            </div>
          </div>
          <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-10 mt-6">
            {/* Study Abroad */}
            <div className="col-span-1">
              <ServiceCard
                title={specializedData?.cards?.[2]?.title || "Study Abroad"}
                image={specializedData?.cards?.[2]?.image_url || "https://images.unsplash.com/photo-1488646953014-85cb44e25828?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"}
                link={specializedData?.cards?.[2]?.link}
              />
            </div>

            {/* Holidays */}
            <div className="col-span-1">
              <ServiceCard
                title={specializedData?.cards?.[3]?.title || "Holidays"}
                image={specializedData?.cards?.[3]?.image_url || "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"}
                link={specializedData?.cards?.[3]?.link}
              />
            </div>

            {/* Medical Testing / Fifth element fallback */}
            <div className="col-span-1">
              <ServiceCard
                title={specializedData?.cards?.[4]?.title || "Medical Testing"}
                image={specializedData?.cards?.[4]?.image_url || "https://images.unsplash.com/photo-1582560469781-1965b9af903d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"}
                link={specializedData?.cards?.[4]?.link}
              />
            </div>

            {/* Explore Other Solutions */}
            <div className="col-span-1">
              <div className="bg-[#2A6ABA] text-primary-white rounded-[20px] h-[280px] p-6 flex flex-col justify-between">
                <h3 className="text-2xl font-bold leading-tight">
                  Explore Other solutions we offer
                </h3>
                <Link to="/ourcoreservices" className="bg-primary-red hover:bg-red-700 text-primary-white font-semibold py-3 px-5  text-sm w-fit">
                  KNOW MORE
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      <img src={Ellipse} alt="Ellipse" className=""/>
    </section>
  );
}
